import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { bookmarkService } from '../services/bookmarkService';

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildActivityData(bookmarks) {
  const counts = {};
  const today = new Date();
  // Initialise last 30 days to zero so days with no bookmarks still show
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    counts[d.toISOString().slice(0, 10)] = 0;
  }
  bookmarks.forEach(b => {
    const day = b.date || (b.createdAt ? b.createdAt.slice(0, 10) : null);
    if (day && counts[day] !== undefined) counts[day]++;
  });
  return Object.entries(counts).map(([date, count]) => ({
    date,
    label: new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    count,
  }));
}

function buildStatusData(bookmarks) {
  const counts = { processed: 0, processing: 0, failed: 0, pending: 0 };
  bookmarks.forEach(b => {
    if (b.status in counts) counts[b.status]++;
    else counts.pending++;
  });
  return [
    { name: 'Processed', value: counts.processed, color: '#10b981' },
    { name: 'Processing', value: counts.processing, color: '#3b82f6' },
    { name: 'Failed', value: counts.failed, color: '#ef4444' },
    { name: 'Pending', value: counts.pending, color: '#9ca3af' },
  ].filter(s => s.value > 0);
}

function buildCategoryData(bookmarks) {
  const counts = {};
  bookmarks.forEach(b => {
    (b.categories || []).forEach(cat => { counts[cat] = (counts[cat] || 0) + 1; });
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([name, value]) => ({ name, value }));
}

// ── Sub-components ─────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{label}</p>
    <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value ?? '—'}</p>
    {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{sub}</p>}
  </div>
);

const SectionHeader = ({ title }) => (
  <h2 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-3">{title}</h2>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 shadow text-sm">
      <p className="font-medium text-gray-700 dark:text-gray-300">{label}</p>
      <p className="text-primary-600 dark:text-primary-400">{payload[0].value} bookmark{payload[0].value !== 1 ? 's' : ''}</p>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────

const ANALYTICS_CAP = 500;

// Fetch up to ANALYTICS_CAP bookmarks for client-side analytics aggregation.
async function fetchBookmarksSample() {
  const all = [];
  let nextToken = null;
  do {
    const response = await bookmarkService.getBookmarks({ limit: 100, nextToken });
    all.push(...(response.items || []));
    nextToken = response.nextToken || null;
  } while (nextToken && all.length < ANALYTICS_CAP);
  return { items: all, truncated: !!nextToken };
}

const Analytics = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [tags, setTags] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchBookmarksSample(),
      bookmarkService.getTags({ limit: 200 }),
    ])
      .then(([{ items, truncated: t }, { items: tagItems }]) => {
        setBookmarks(items);
        setTruncated(t);
        setTags(tagItems);
      })
      .catch(() => setError('Failed to load analytics data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return <p className="text-center py-20 text-gray-500">{error}</p>;
  }

  const activityData = buildActivityData(bookmarks);
  const statusData = buildStatusData(bookmarks);
  const categoryData = buildCategoryData(bookmarks);

  const totalBookmarks = bookmarks.length;
  const processedCount = bookmarks.filter(b => b.status === 'processed').length;
  const successRate = totalBookmarks > 0 ? Math.round((processedCount / totalBookmarks) * 100) : 0;
  const readLaterCount = bookmarks.filter(b => b.readLater).length;

  // Last 30-day total
  const last30Days = activityData.reduce((sum, d) => sum + d.count, 0);

  // Top tags by count
  const topTags = [...tags]
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, 20);

  const maxTagCount = topTags.length > 0 ? topTags[0].count || 1 : 1;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analytics</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">An overview of your bookmark library</p>
      </div>

      {truncated && (
        <div className="mb-5 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-sm rounded-lg px-4 py-2.5">
          Charts reflect your most recent {ANALYTICS_CAP} bookmarks. Counts and percentages may differ from your full library.
        </div>
      )}

      {/* Summary stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total bookmarks" value={totalBookmarks} />
        <StatCard label="Saved last 30 days" value={last30Days} />
        <StatCard label="Processing success" value={`${successRate}%`} sub={`${processedCount} of ${totalBookmarks} processed`} />
        <StatCard label="Read later" value={readLaterCount} sub={totalBookmarks > 0 ? `${Math.round((readLaterCount / totalBookmarks) * 100)}% of library` : undefined} />
      </div>

      {/* Activity chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <SectionHeader title="Bookmarks saved — last 30 days" />
        {last30Days === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center">No bookmarks saved in the last 30 days.</p>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={activityData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
                interval={4}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f3f4f6' }} />
              <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                {activityData.map((entry, i) => (
                  <Cell key={i} fill={entry.count > 0 ? '#6366f1' : '#e5e7eb'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Processing status */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <SectionHeader title="Processing status" />
          {statusData.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No data yet.</p>
          ) : (
            <div className="space-y-3">
              {statusData.map(s => (
                <div key={s.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-300 font-medium">{s.name}</span>
                    <span className="text-gray-500 dark:text-gray-400">{s.value} ({Math.round((s.value / totalBookmarks) * 100)}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${(s.value / totalBookmarks) * 100}%`, backgroundColor: s.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top categories */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <SectionHeader title="Top categories" />
          {categoryData.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No categories found. Add bookmarks with category data to see this chart.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={categoryData} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} tickLine={false} axisLine={false} />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: '#f3f4f6' }} formatter={(v) => [v, 'bookmarks']} />
                <Bar dataKey="value" fill="#8b5cf6" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tag frequency */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <SectionHeader title="Tag frequency" />
        {topTags.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No tags yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {topTags.map(tag => {
              const ratio = (tag.count || 1) / maxTagCount;
              const size = ratio > 0.75 ? 'text-lg' : ratio > 0.5 ? 'text-base' : ratio > 0.25 ? 'text-sm' : 'text-xs';
              const opacity = ratio > 0.75 ? 'opacity-100' : ratio > 0.5 ? 'opacity-80' : ratio > 0.25 ? 'opacity-60' : 'opacity-50';
              return (
                <span
                  key={tag.tagName}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium ${size} ${opacity}`}
                  title={`${tag.count || 0} bookmark${tag.count !== 1 ? 's' : ''}`}
                >
                  {tag.tagName}
                  <span className="text-primary-400 dark:text-primary-500 font-normal">{tag.count || 0}</span>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
